import React, { useState, useRef, useMemo, useCallback, useEffect } from "react";
import { Button, Input, Row, Col, Spinner } from "reactstrap";
import Select from "components/Select";
import EmailDocumentPicker from "components/EmailDocumentPicker";
import { CKEditor } from "ckeditor4-react";
import { getActiveColorStyle } from "utils/colorUtils";
import { getSelectStyles } from "utils/selectStyles";
import { useGetCommunicationsTemplates } from "api/communications-templates/service";
import { useGetCalculusDetail } from "api/calculuses/service";
import { useGetDealDetail } from "api/deals/service";
import useSecureMediaUrl from "hooks/useSecureMediaUrl";
import {
  getEmailSubjectSuggestions,
  addEmailSubjectSuggestion,
  filterTextSuggestions,
} from "utils/paymentCommentSuggestions";
import {
  MAX_EMAIL_ATTACHMENT_BYTES,
  MAX_EMAIL_ATTACHMENTS,
  fileLabel,
  fileTone,
  formatBytes,
  isAcceptedEmailFile,
  isImageName,
  readFileAsBase64,
} from "utils/emailAttachments";

const FIELD_HEIGHT = 38;

const pickLibraryList = (payload, key) => {
  if (!payload) return [];
  if (Array.isArray(payload[key])) return payload[key];
  if (Array.isArray(payload?.data?.[key])) return payload.data[key];
  return [];
};

const AttachmentPreview = ({ file }) => {
  const { blobUrl } = useSecureMediaUrl(file.mediaUrl, {
    enabled: !file.previewUrl && !!file.mediaUrl && file.isImage,
  });
  const src = file.previewUrl || blobUrl;
  if (file.isImage && src) {
    return (
      <img
        src={src}
        alt={file.name}
        style={{ width: 52, height: 52, objectFit: "cover", flex: "0 0 52px", background: "#111" }}
      />
    );
  }
  const tone = fileTone(file.name);
  return (
    <div
      style={{
        width: 52,
        height: 52,
        flex: "0 0 52px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: `${tone}22`,
        color: tone,
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: "0.04em",
      }}
    >
      {fileLabel(file.name)}
    </div>
  );
};

const SendEmail = ({
  onCancel,
  onSend,
  customerEmail,
  isLightTheme = true,
  isLoading = false,
  activeColor,
  initialFiles = [],
  initialSubject = "",
  initialMessage = "",
  dataSource = "accounts",
  librarySource,
  recordId,
}) => {
  const activeColorStyle = useMemo(() => getActiveColorStyle(activeColor), [activeColor]);
  const [subject, setSubject] = useState(initialSubject || "");
  const [template, setTemplate] = useState(null);
  const [message, setMessage] = useState(initialMessage || "");
  const [files, setFiles] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [attachError, setAttachError] = useState("");
  const [isReadingFiles, setIsReadingFiles] = useState(false);
  const editorRef = useRef(null);
  const fileInputRef = useRef(null);
  const previewUrlsRef = useRef([]);
  const resolvedLibrarySource = librarySource || dataSource;
  const isAccountLibrary = resolvedLibrarySource !== "deals" && !!recordId;
  const isDealLibrary = resolvedLibrarySource === "deals" && !!recordId;

  const { data: accountFilesData, isLoading: accountFilesLoading, isFetching: accountFilesFetching } =
    useGetCalculusDetail(isAccountLibrary ? recordId : undefined, "files", undefined, isAccountLibrary);
  const { data: accountEcabinetData, isLoading: accountEcabinetLoading, isFetching: accountEcabinetFetching } =
    useGetCalculusDetail(isAccountLibrary ? recordId : undefined, "ecabinet", undefined, isAccountLibrary);
  const { data: dealFilesData, isLoading: dealFilesLoading, isFetching: dealFilesFetching } =
    useGetDealDetail(isDealLibrary ? recordId : undefined, isDealLibrary ? "files" : undefined);
  const { data: dealEcabinetData, isLoading: dealEcabinetLoading, isFetching: dealEcabinetFetching } =
    useGetDealDetail(isDealLibrary ? recordId : undefined, isDealLibrary ? "ecabinet" : undefined);

  const libraryFiles = isDealLibrary
    ? pickLibraryList(dealFilesData, "files")
    : pickLibraryList(accountFilesData, "files");
  const libraryEcabinets = isDealLibrary
    ? pickLibraryList(dealEcabinetData, "ecabinets")
    : pickLibraryList(accountEcabinetData, "ecabinets");
  const libraryLoading = isDealLibrary
    ? dealFilesLoading || dealEcabinetLoading || dealFilesFetching || dealEcabinetFetching
    : accountFilesLoading || accountEcabinetLoading || accountFilesFetching || accountEcabinetFetching;

  const [subjectSuggestionsOpen, setSubjectSuggestionsOpen] = useState(false);
  const [subjectSuggestionHighlight, setSubjectSuggestionHighlight] = useState(-1);
  const [subjectSuggestionVersion, setSubjectSuggestionVersion] = useState(0);
  const subjectBlurTimerRef = useRef(null);

  const idleBorder = isLightTheme ? "#ced4da" : "#2b3553";

  const templateSelectStyles = useMemo(() => {
    const base = getSelectStyles(activeColorStyle, isLightTheme);
    return {
      ...base,
      control: (provided, state) => ({
        ...base.control(provided, state),
        borderRadius: 0,
        minHeight: FIELD_HEIGHT,
        height: FIELD_HEIGHT,
        borderWidth: 1,
        borderStyle: "solid",
        borderColor: state?.isFocused
          ? activeColorStyle.borderColor
          : idleBorder,
        boxShadow: "none",
        backgroundColor: "transparent",
        "&:hover": {
          borderColor: state?.isFocused
            ? activeColorStyle.borderColor
            : idleBorder,
        },
      }),
      valueContainer: (provided) => ({
        ...provided,
        height: FIELD_HEIGHT - 2,
        padding: "0 8px",
      }),
      indicatorsContainer: (provided) => ({
        ...provided,
        height: FIELD_HEIGHT - 2,
      }),
      dropdownIndicator: (provided, state) => ({
        ...base.dropdownIndicator(provided, state || {}),
        padding: "0 8px",
      }),
      clearIndicator: (provided, state) => ({
        ...base.clearIndicator(provided, state || {}),
        padding: "0 4px",
      }),
      singleValue: (provided, state) => ({
        ...base.singleValue(provided, state || {}),
        color: isLightTheme ? "#333" : "#fff",
        fontSize: 12,
      }),
      placeholder: (provided, state) => ({
        ...base.placeholder(provided, state || {}),
        color: isLightTheme ? "#6c757d" : "rgba(255,255,255,0.5)",
        fontSize: 12,
      }),
      input: (provided) => ({
        ...provided,
        margin: 0,
        padding: 0,
        color: isLightTheme ? "#333" : "#fff",
      }),
    };
  }, [activeColorStyle, isLightTheme, idleBorder]);

  useEffect(() => {
    return () => {
      if (subjectBlurTimerRef.current) {
        clearTimeout(subjectBlurTimerRef.current);
      }
      previewUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
    };
  }, []);

  const removeAttachment = useCallback((key) => {
    setFiles((prev) => {
      const target = prev.find((file) => file.key === key);
      if (target?.previewUrl) {
        URL.revokeObjectURL(target.previewUrl);
        previewUrlsRef.current = previewUrlsRef.current.filter((url) => url !== target.previewUrl);
      }
      return prev.filter((file) => file.key !== key);
    });
  }, []);

  const handleNewFiles = useCallback(async (fileList) => {
    const incoming = Array.from(fileList || []);
    if (!incoming.length) return;
    setAttachError("");
    setIsReadingFiles(true);
    const next = [];
    const errors = [];
    try {
      for (const file of incoming) {
        if (!isAcceptedEmailFile(file)) {
          errors.push(`${file.name} is not a supported file type.`);
          continue;
        }
        if (file.size > MAX_EMAIL_ATTACHMENT_BYTES) {
          errors.push(`${file.name} is larger than 15 MB.`);
          continue;
        }
        const duplicate = files.some(
          (item) => item.source === "upload" && item.name === file.name && item.size === file.size
        );
        if (duplicate) continue;
        if (files.length + next.length >= MAX_EMAIL_ATTACHMENTS) {
          errors.push(`You can attach up to ${MAX_EMAIL_ATTACHMENTS} documents.`);
          break;
        }
        const base64 = await readFileAsBase64(file);
        const isImage = isImageName(file.name, file.type);
        const previewUrl = isImage ? URL.createObjectURL(file) : null;
        if (previewUrl) previewUrlsRef.current.push(previewUrl);
        next.push({
          key: `upload-${file.name}-${file.size}-${file.lastModified}`,
          name: file.name,
          contentType: file.type || "application/octet-stream",
          size: file.size,
          source: "upload",
          isImage,
          previewUrl,
          mediaUrl: null,
          base64,
          mediaFileId: null,
        });
      }
      if (next.length) setFiles((prev) => [...prev, ...next]);
      if (errors.length) setAttachError(errors[0]);
    } catch {
      next.forEach((file) => {
        if (!file.previewUrl) return;
        URL.revokeObjectURL(file.previewUrl);
        previewUrlsRef.current = previewUrlsRef.current.filter((url) => url !== file.previewUrl);
      });
      setAttachError("Could not read the selected file.");
    } finally {
      setIsReadingFiles(false);
    }
  }, [files]);

  const handleLibraryConfirm = useCallback((chosen) => {
    setFiles((prev) => {
      const uploads = prev.filter((file) => file.source === "upload");
      const library = chosen.map((item) => {
        const name = item?.media?.name || "document";
        const mime = item?.media?.mime || "";
        return {
          key: `library-${item.id}`,
          name,
          contentType: mime || "application/octet-stream",
          size: Number(item?.media?.size) ? Number(item.media.size) * 1024 : 0,
          source: item.source,
          isImage: isImageName(name, mime),
          previewUrl: null,
          mediaUrl: item?.media?.url || "",
          base64: null,
          mediaFileId: Number(item.id),
        };
      });
      return [...uploads, ...library].slice(0, MAX_EMAIL_ATTACHMENTS);
    });
    setPickerOpen(false);
    setAttachError("");
  }, []);

  const { data: templatesData } = useGetCommunicationsTemplates({ data_source: dataSource });

  const templateOptions = useMemo(() => {
    const options = [{ label: "-- None Selected --", value: "" }];
    if (templatesData) {
      const results = Array.isArray(templatesData)
        ? templatesData
        : templatesData.results || templatesData.data || [];
      results.forEach((t) => {
        options.push({ label: t.name, value: t.id, layout: t.layout });
      });
    }
    return options;
  }, [templatesData]);

  const filteredSubjectSuggestions = useMemo(
    () => filterTextSuggestions(getEmailSubjectSuggestions(), subject),
    [subject, subjectSuggestionVersion]
  );

  const openSubjectSuggestions = useCallback(() => {
    if (subjectBlurTimerRef.current) {
      clearTimeout(subjectBlurTimerRef.current);
      subjectBlurTimerRef.current = null;
    }
    setSubjectSuggestionsOpen(true);
    setSubjectSuggestionHighlight(-1);
  }, []);

  const closeSubjectSuggestions = useCallback(() => {
    setSubjectSuggestionsOpen(false);
    setSubjectSuggestionHighlight(-1);
  }, []);

  const selectSubjectSuggestion = useCallback(
    (text) => {
      setSubject(text);
      closeSubjectSuggestions();
    },
    [closeSubjectSuggestions]
  );

  const handleSubjectKeyDown = useCallback(
    (e) => {
      if (!subjectSuggestionsOpen || filteredSubjectSuggestions.length === 0) {
        if (e.key === "Escape") closeSubjectSuggestions();
        return;
      }
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSubjectSuggestionHighlight((prev) =>
          prev < filteredSubjectSuggestions.length - 1 ? prev + 1 : 0
        );
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setSubjectSuggestionHighlight((prev) =>
          prev > 0 ? prev - 1 : filteredSubjectSuggestions.length - 1
        );
      } else if (e.key === "Enter" && subjectSuggestionHighlight >= 0) {
        e.preventDefault();
        selectSubjectSuggestion(
          filteredSubjectSuggestions[subjectSuggestionHighlight]
        );
      } else if (e.key === "Escape") {
        e.preventDefault();
        closeSubjectSuggestions();
      }
    },
    [
      subjectSuggestionsOpen,
      filteredSubjectSuggestions,
      subjectSuggestionHighlight,
      selectSubjectSuggestion,
      closeSubjectSuggestions,
    ]
  );

  const handleTemplateChange = (option) => {
    setTemplate(option);
    const newContent = option && option.layout ? option.layout : "";

    if (editorRef.current) {
      editorRef.current.setData(newContent);
    }
    setMessage(newContent);
  };

  const handleComplete = () => {
    const finalSubject = subject.trim();
    if (finalSubject) {
      addEmailSubjectSuggestion(finalSubject);
      setSubjectSuggestionVersion((v) => v + 1);
    }
    onSend({
      subject,
      template,
      message: editorRef.current?.getData() || message,
      attachments: files.map((file) =>
        file.mediaFileId
          ? { mediaFileId: file.mediaFileId, filename: file.name }
          : {
              filename: file.name,
              content: file.base64,
              contentType: file.contentType,
            }
      ),
    });
  };

  const inputStyle = {
    height: FIELD_HEIGHT,
    minHeight: FIELD_HEIGHT,
    fontSize: 12,
    borderColor: idleBorder,
  };

  return (
    <div>
      <div className="d-flex align-items-center justify-content-between mb-2">
        <h6
          className="mb-0 font-weight-bold text-uppercase"
          style={{ fontSize: "14px", letterSpacing: "0.05em" }}
        >
          SEND EMAIL
        </h6>
        <div>
          <Button
            size="sm"
            color="secondary"
            outline
            className="mr-2 mb-0"
            onClick={onCancel}
            disabled={isLoading}
            style={{ fontSize: "11px", fontWeight: "600", padding: "6px 16px" }}
          >
            CANCEL
          </Button>
          <Button
            size="sm"
            color="info"
            className="mb-0"
            onClick={handleComplete}
            disabled={isLoading || isReadingFiles || !subject.trim()}
            style={{ fontSize: "11px", fontWeight: "600", padding: "6px 16px" }}
          >
            {isLoading ? <Spinner size="sm" className="mr-1" /> : null}
            COMPLETE
          </Button>
        </div>
      </div>

      <div className="mb-3">
        <span className="font-weight-bold" style={{ color: "#ff3d3d", fontSize: "12px", lineHeight: 1.45 }}>
          WARNING: You agree not to send any information protected under the Gramm-Leach-Bliley Act
          through DealertIQ Email.
        </span>
      </div>

      <Row className="mb-2 align-items-end">
        <Col md="6">
          <label
            style={{
              fontSize: "12px",
              marginBottom: "4px",
              display: "block",
              fontWeight: "bold",
            }}
          >
            Email Subject
          </label>
          <div style={{ position: "relative" }}>
            {subjectSuggestionsOpen && filteredSubjectSuggestions.length > 0 && (
              <div
                style={{
                  position: "absolute",
                  left: 0,
                  right: 0,
                  top: "100%",
                  marginTop: 2,
                  maxHeight: 160,
                  overflowY: "auto",
                  zIndex: 30,
                  background: isLightTheme ? "#fff" : "#27293d",
                  border: isLightTheme
                    ? "1px solid #ced4da"
                    : "1px solid rgba(255,255,255,0.2)",
                  boxShadow: isLightTheme
                    ? "0 2px 8px rgba(0,0,0,0.12)"
                    : "0 2px 8px rgba(0,0,0,0.4)",
                }}
              >
                {filteredSubjectSuggestions.map((item, idx) => (
                  <button
                    key={`${item}-${idx}`}
                    type="button"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      selectSubjectSuggestion(item);
                    }}
                    onMouseEnter={() => setSubjectSuggestionHighlight(idx)}
                    style={{
                      display: "block",
                      width: "100%",
                      textAlign: "left",
                      padding: "6px 10px",
                      border: "none",
                      background:
                        subjectSuggestionHighlight === idx
                          ? activeColorStyle?.borderColor || "#1d8cf8"
                          : "transparent",
                      color:
                        subjectSuggestionHighlight === idx
                          ? "#fff"
                          : isLightTheme
                            ? "#333"
                            : "#eee",
                      fontSize: 12,
                      fontWeight: 600,
                      cursor: "pointer",
                    }}
                  >
                    {item}
                  </button>
                ))}
              </div>
            )}
            <Input
              type="text"
              className="form-control"
              value={subject}
              style={inputStyle}
              onChange={(e) => {
                setSubject(e.target.value);
                setSubjectSuggestionsOpen(true);
                setSubjectSuggestionHighlight(-1);
              }}
              onFocus={openSubjectSuggestions}
              onBlur={() => {
                subjectBlurTimerRef.current = setTimeout(() => {
                  closeSubjectSuggestions();
                }, 150);
              }}
              onKeyDown={handleSubjectKeyDown}
              autoComplete="off"
              placeholder="Email subject"
            />
          </div>
        </Col>
        <Col md="6">
          <label
            style={{
              fontSize: "12px",
              marginBottom: "4px",
              display: "block",
              fontWeight: "bold",
            }}
          >
            Template
          </label>
          <Select
            activeColor={activeColor}
            options={templateOptions}
            value={template}
            onChange={handleTemplateChange}
            placeholder="-- None Selected --"
            isSearchable
            isClearable
            className="react-select"
            styles={templateSelectStyles}
          />
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md="12">
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept=".pdf,.xls,.xlsx,.xlsm,.csv,.doc,.docx,.ppt,.pptx,.txt,.png,.jpg,.jpeg,.gif,.webp,.bmp,.tif,.tiff,image/*"
            style={{ display: "none" }}
            onChange={(event) => {
              handleNewFiles(event.target.files);
              event.target.value = "";
            }}
          />
          {files.length > 0 && (
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 8,
                marginBottom: 10,
              }}
            >
              {files.map((file) => {
                const sourceLabel =
                  file.source === "ecabinet" ? "eCabinet" : file.source === "files" ? "Files" : "New";
                return (
                  <div
                    key={file.key}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      maxWidth: 280,
                      padding: 6,
                      background: isLightTheme ? "#f4f6fb" : "#27293d",
                      border: `1px solid ${isLightTheme ? "#d9dee7" : "rgba(255,255,255,0.14)"}`,
                      borderLeft: `3px solid ${activeColorStyle?.borderColor || "#1d8cf8"}`,
                    }}
                  >
                    <AttachmentPreview file={file} />
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div
                        title={file.name}
                        style={{
                          fontSize: 12,
                          fontWeight: 700,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {file.name}
                      </div>
                      <div
                        style={{
                          fontSize: 11,
                          color: isLightTheme ? "#6c757d" : "rgba(255,255,255,0.62)",
                        }}
                      >
                        {sourceLabel}
                        {file.size ? ` · ${formatBytes(file.size)}` : ""}
                      </div>
                    </div>
                    <button
                      type="button"
                      aria-label={`Remove ${file.name}`}
                      onClick={() => removeAttachment(file.key)}
                      disabled={isLoading}
                      style={{
                        border: "none",
                        background: "transparent",
                        color: isLightTheme ? "#6c757d" : "rgba(255,255,255,0.7)",
                        fontSize: 16,
                        lineHeight: 1,
                        cursor: "pointer",
                        padding: "0 4px",
                      }}
                    >
                      ×
                    </button>
                  </div>
                );
              })}
            </div>
          )}
          {attachError ? (
            <div style={{ color: "#ff3d3d", fontSize: 12, marginBottom: 8 }}>{attachError}</div>
          ) : null}
          <div className="d-flex justify-content-end" style={{ gap: 8 }}>
            <Button
              type="button"
              size="sm"
              color="info"
              outline
              className="mb-0"
              disabled={isLoading || isReadingFiles}
              onClick={() => fileInputRef.current?.click()}
              style={{ padding: "6px 12px", fontSize: "11px", fontWeight: "600" }}
            >
              {isReadingFiles ? <Spinner size="sm" className="mr-1" /> : null}
              ATTACH NEW DOCUMENT
            </Button>
            <Button
              type="button"
              size="sm"
              color="info"
              outline
              className="mb-0"
              disabled={isLoading || !recordId}
              onClick={() => setPickerOpen(true)}
              style={{ padding: "6px 12px", fontSize: "11px", fontWeight: "600" }}
            >
              ATTACH ECABINET DOCUMENT
            </Button>
          </div>
        </Col>
      </Row>

      <div className="mb-2">
        <label style={{ fontSize: "12px" }}>Message:</label>
        <div className="ckeditor-container" style={{ border: `1px solid ${idleBorder}` }}>
          <style>{`
            .ckeditor-container {
              overflow: hidden;
              background: #fff;
            }
            .cke_chrome {
              border: none !important;
              box-shadow: none !important;
              padding: 0 !important;
            }
            .cke_inner {
              background: #fff !important;
            }
            .cke_top { 
              background: #d4e0ef !important;
              padding: 6px !important;
              border-bottom: 1px solid #aebfd5 !important;
            }
            .cke_bottom {
              display: none !important;
            }
            .cke_toolgroup {
              margin-bottom: 5px !important;
            }
          `}</style>
          <CKEditor
            editorUrl="https://cdn.ckeditor.com/4.16.2/full/ckeditor.js"
            initData={message}
            config={{
              toolbar: [
                {
                  name: "document",
                  items: [
                    "Source",
                    "-",
                    "Save",
                    "NewPage",
                    "Preview",
                    "Print",
                    "-",
                    "Templates",
                  ],
                },
                {
                  name: "clipboard",
                  items: [
                    "Cut",
                    "Copy",
                    "Paste",
                    "PasteText",
                    "PasteFromWord",
                    "-",
                    "Undo",
                    "Redo",
                  ],
                },
                {
                  name: "editing",
                  items: ["Find", "Replace", "-", "SelectAll", "-", "Scayt"],
                },
                {
                  name: "forms",
                  items: [
                    "Form",
                    "Checkbox",
                    "Radio",
                    "TextField",
                    "Textarea",
                    "Select",
                    "Button",
                    "ImageButton",
                    "HiddenField",
                  ],
                },
                "/",
                {
                  name: "basicstyles",
                  items: [
                    "Bold",
                    "Italic",
                    "Underline",
                    "Strike",
                    "Subscript",
                    "Superscript",
                    "-",
                    "CopyFormatting",
                    "RemoveFormat",
                  ],
                },
                {
                  name: "paragraph",
                  items: [
                    "NumberedList",
                    "BulletedList",
                    "-",
                    "Outdent",
                    "Indent",
                    "-",
                    "Blockquote",
                    "CreateDiv",
                    "-",
                    "JustifyLeft",
                    "JustifyCenter",
                    "JustifyRight",
                    "JustifyBlock",
                    "-",
                    "BidiLtr",
                    "BidiRtl",
                    "Language",
                  ],
                },
                { name: "links", items: ["Link", "Unlink", "Anchor"] },
                {
                  name: "insert",
                  items: [
                    "Image",
                    "Flash",
                    "Table",
                    "HorizontalRule",
                    "Smiley",
                    "SpecialChar",
                    "PageBreak",
                    "Iframe",
                  ],
                },
                "/",
                {
                  name: "styles",
                  items: ["Styles", "Format", "Font", "FontSize"],
                },
                { name: "colors", items: ["TextColor", "BGColor"] },
                { name: "tools", items: ["Maximize", "ShowBlocks"] },
                { name: "about", items: ["About"] },
              ],
              height: 400,
              removePlugins: "exportpdf,elementspath",
              resize_enabled: false,
              skin: "moono-lisa",
              versionCheck: false,
              uiColor: "#dae5f3",
              language: "en",
              allowedContent: true,
            }}
            onInstanceReady={({ editor }) => {
              editorRef.current = editor;
            }}
            onChange={(evt) => {
              setMessage(evt.editor.getData());
            }}
          />
        </div>
      </div>
      <EmailDocumentPicker
        isOpen={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onConfirm={handleLibraryConfirm}
        isLightTheme={isLightTheme}
        accent={activeColorStyle?.borderColor || "#1d8cf8"}
        files={libraryFiles}
        ecabinets={libraryEcabinets}
        isLoading={libraryLoading && pickerOpen}
        selectedIds={files.filter((file) => file.mediaFileId).map((file) => file.mediaFileId)}
        recordLabel={resolvedLibrarySource === "deals" ? "deal" : "account"}
      />
    </div>
  );
};

export default SendEmail;
